import { useEffect, useMemo, useState } from "react";

import { getEffectiveVesselAccess } from "../api/auth";
import type { AuthUser, EffectiveVesselAccess } from "../types/auth";
import { useAuth } from "./useAuth";
import { useOfficeVesselScope } from "./useOfficeVesselScope";

interface ActivePurchaseAccessResult {
  user: AuthUser | null;
  primaryUser: AuthUser | null;
  effectiveAccess: EffectiveVesselAccess | null;
  isLoading: boolean;
}

const effectiveAccessCache = new Map<string, EffectiveVesselAccess>();

export function useActivePurchaseAccess(): ActivePurchaseAccessResult {
  const { user } = useAuth();
  const { selectedVesselId } = useOfficeVesselScope();
  const shouldFetch = user?.type === "office" && selectedVesselId !== null;
  const primaryAccessFingerprint = JSON.stringify([user?.role, user?.roles, user?.form_ids, user?.process_ids]);
  const cacheKey = `${user?.id ?? "anonymous"}:${selectedVesselId ?? "none"}:${primaryAccessFingerprint}`;
  const [effectiveAccess, setEffectiveAccess] = useState<EffectiveVesselAccess | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!shouldFetch || selectedVesselId === null) {
      setEffectiveAccess(null);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const cachedAccess = effectiveAccessCache.get(cacheKey);
    if (cachedAccess) {
      setEffectiveAccess(cachedAccess);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setIsLoading(true);
    getEffectiveVesselAccess(selectedVesselId)
      .then((access) => {
        if (cancelled) {
          return;
        }
        effectiveAccessCache.set(cacheKey, access);
        setEffectiveAccess(access);
      })
      .catch(() => {
        if (!cancelled) {
          setEffectiveAccess(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, selectedVesselId, shouldFetch]);

  const activeUser = useMemo<AuthUser | null>(() => {
    if (!user) {
      return null;
    }
    if (!shouldFetch) {
      return user;
    }
    if (!effectiveAccess) {
      return null;
    }
    const effectiveRoleCodes = Array.from(
      new Set([user.role, ...effectiveAccess.effective_role_codes].filter(Boolean)),
    );
    return {
      ...user,
      role: user.role,
      roles: effectiveRoleCodes,
      vessel_id: effectiveAccess.vessel_id,
      vessel_name: effectiveAccess.vessel_name,
      vessel_code: effectiveAccess.vessel_code,
      form_ids: effectiveAccess.form_ids,
      process_ids: effectiveAccess.process_ids,
    };
  }, [effectiveAccess, shouldFetch, user]);

  return {
    user: activeUser,
    primaryUser: user ?? null,
    effectiveAccess,
    isLoading,
  };
}
